import { AfterViewInit, Component, effect, ElementRef, input, OnDestroy, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { generateRandomAlternativeNumberings, generateRandomAnnotations } from './viewer/smart-generator';
import { AlternativeNumbering, SmartSequenceAnnotation, SmartSequenceVisOptions } from './viewer/data-processing/seq-viewer-models';
import { SmartSequenceVisualisation } from './viewer/sequence-visualisation';
import { validateAlternativeNumberings, validateAnnotations, validateNonObserved } from './viewer/data-processing/seq-viewer-validation';

@Component({
  selector: 'lib-smart-seq-viewer',
  imports: [CommonModule],
  templateUrl: './smart-seq-viewer.component.html',
  styleUrl: './smart-seq-viewer.component.scss',
})
export class SmartSeqViewerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('containerElement', { read: ElementRef }) public containerElement!: ElementRef;

  // External inputs (read-only)
  public readonly containerId = input<string>('smart-seq-id');
  public readonly sequence = input<string>('TESTSTRING');
  public readonly altSequences = input<AlternativeNumbering[]>([]);
  public readonly nonObserved = input<number[]>([]);
  public readonly entityId = input<string | undefined>(undefined);
  public readonly chainId = input<string | undefined>(undefined);
  public readonly backgroundDataInput = input<SmartSequenceAnnotation | undefined>(undefined);
  public readonly underlineDataInput = input<SmartSequenceAnnotation | undefined>(undefined);
  public readonly circleAboveDataInput = input<SmartSequenceAnnotation | undefined>(undefined);
  public readonly indexWithMultipleResidues = input<{
    [key: string]: {
      three_letter_code: string;
      one_letter_code: string;
      parent_chem_comp_ids: string[];
    };
  }>({});

  public readonly options = input<SmartSequenceVisOptions | undefined>({
    grouping: true,
    groupingLineBreak: false,
    externalEvents: true,
    hoverTooltips: false,
    useAuthNumbers: true,
    helpLogoSrc: '/assets/img/help_outline_24px.svg',
  });
  public readonly isNucleic = input<boolean>(false);

  // Internally mutable copies
  private backgroundData = signal<SmartSequenceAnnotation | undefined>(undefined);
  private underlineData = signal<SmartSequenceAnnotation | undefined>(undefined);
  private circleAboveData = signal<SmartSequenceAnnotation | undefined>(undefined);
  private altSequencesData = signal<AlternativeNumbering[]>([]);
  private nonObservedData = signal<number[]>([]);
  private indexWithMultipleResiduesData = signal<{
    [key: string]: {
      three_letter_code: string;
      one_letter_code: string;
      parent_chem_comp_ids: string[];
    };
  }>({});

  // Instance reference to cleanup
  private visInstance?: SmartSequenceVisualisation;
  private resizeTimeoutId?: any;

  // private generateRandomAnnotations() {
  //   this.backgroundData.set(generateRandomAnnotations(1, this.sequence(), 'Background'));
  //   this.underlineData.set(generateRandomAnnotations(2, this.sequence(), 'Underline'));
  //   this.circleAboveData.set(generateRandomAnnotations(3, this.sequence(), 'CircleAbove'));
  //   const altSequences = generateRandomAlternativeNumberings(this.sequence(), true, true);
  // }
  constructor() {
    effect(() => {
      // Re-run whenever ANY input used here changes
      this.initVisualisation();
    });
  }

  ngAfterViewInit(): void {
    this.initVisualisation();
  }

  private initVisualisation() {
    // const sequence = 'M'.repeat(1000);
    // const sequence = generateRandomSequence(5000);
    // const sequence = 'IVGGYNCEENSVPYQVSLNSGYHFCGGSLINEQWVVSAGHCYKSRIQVRLGEHNIEVLEGNEQFINAAKIIRHPQYDRKTLNNDIMLIKLSSRAVINARVSTISLPTAPPATGTKCLISGWGNTASSGADYPDELQCLDAPVLSQAKCEASYPGKITSNMFCVGFLEGGKDSCQGDSGGPVVCNGQLQGVVSWGDGCAQKNKPGVYTKVYNYVKWIKNTIAANS';

    // Read every input this effect needs to react to BEFORE the containerElement guard below.
    // The constructor's effect() can fire before ngAfterViewInit (Angular signal effects
    // aren't guaranteed to run after view init), in which case containerElement isn't resolved
    // yet and we bail out below — but an effect only re-runs for signals it actually read
    // during a given execution, so if these reads sat after the early return, that first
    // (bailed-out) run would track nothing and the effect would never fire again for any later
    // input change (e.g. switching chains), even though ngAfterViewInit's own direct call still
    // renders the very first chain correctly and masks the problem until you try to switch.
    const sequence = this.sequence();
    const chainId = this.chainId();
    const entityId = this.entityId();
    const altSequences = this.altSequences();
    const nonObserved = this.nonObserved();
    const backgroundDataInput = this.backgroundDataInput();
    const underlineDataInput = this.underlineDataInput();
    const circleAboveDataInput = this.circleAboveDataInput();
    const indexWithMultipleResidues = this.indexWithMultipleResidues();
    const options = this.options();
    const isNucleic = this.isNucleic();

    if (!this.containerElement?.nativeElement) return;

    // Initialize internal signals from input
    this.backgroundData.set(backgroundDataInput);
    this.underlineData.set(underlineDataInput);
    this.circleAboveData.set(circleAboveDataInput);
    this.indexWithMultipleResiduesData.set(indexWithMultipleResidues);

    const validAlt = validateAlternativeNumberings(sequence, altSequences, true).valid;
    const validNonObs = validateNonObserved(sequence, nonObserved, true).valid;

    if (validAlt) this.altSequencesData.set(altSequences);
    else this.altSequencesData.set([]);

    if (validNonObs) this.nonObservedData.set(nonObserved);
    else this.nonObservedData.set([]);

    const annotations = [this.backgroundData(), this.underlineData(), this.circleAboveData()].filter((annotation) => annotation !== undefined);

    if (options && isNucleic) {
      options.isNucleic = true;
    }
    if (this.visInstance) {
      if (this.resizeTimeoutId) clearTimeout(this.resizeTimeoutId);
      this.visInstance.destroy();
      this.visInstance = undefined;
    }

    this.visInstance = new SmartSequenceVisualisation(
      sequence,
      this.containerElement.nativeElement,
      this.altSequencesData(),
      this.nonObservedData(),
      annotations,
      this.indexWithMultipleResiduesData(),
      entityId,
      chainId,
      options
    );
    this.waitForCanvasAndResize();
  }

  private waitForCanvasAndResize(): void {
    if (!this.containerElement) return;
    const element = this.containerElement.nativeElement;
    if (!element) return;
    const canvasElement = element.querySelector('canvas');
    if (canvasElement) {
      this.visInstance?.onContainerResize(); // Call resize when canvas is available
      if (this.resizeTimeoutId) clearTimeout(this.resizeTimeoutId);
    } else if (this.visInstance) {
      // If canvas is not found, retry after a big delay (e.g., 1s)
      this.resizeTimeoutId = setTimeout(() => this.waitForCanvasAndResize(), 1000);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeTimeoutId) clearTimeout(this.resizeTimeoutId);
    this.visInstance?.destroy();
    this.visInstance = undefined;
  }

  // Lets a host component (e.g. a table row click elsewhere on the page) select a residue in
  // this viewer directly — its own canvas highlight and right-side residue-details sidebar
  // both update, same as a real click on the canvas. residueIndex is this viewer's own local
  // 1-based numbering (matching `sequence`'s indexing), not any external/absolute numbering.
  // doNotPropagate/doNotReport: true — this is host-driven, not a genuine user click on THIS
  // canvas, so it must not re-dispatch the library's own outward click event (which would loop
  // back into whatever's listening for it, e.g. this same host's protvista-click handler).
  public selectResidue(residueIndex: number): void {
    this.visInstance?.eventManager.selectResidueState(residueIndex, true, true);
    this.visInstance?.draw();
  }

  // Host-driven deselect (clears the highlight and the residue-details panel). doNotReport so it
  // doesn't dispatch smartSeqViewerUnselect back to the host that asked for it.
  public clearResidueSelection(): void {
    this.visInstance?.eventManager.unselectResidueState(true);
  }
}
